import type {
  CreditBalance,
  Entitlement,
  PublisherReleasePlatform,
  PublisherReleaseStatus,
  PublisherReleaseType,
  User,
} from './domain';
import { PACKAGE_KEYS, type PackageKey } from '../lib/accessLevel';

export interface AdminUserSummary {
  user: User;
  entitlements: Entitlement;
  level: number;
  /** ISO 8601 account creation timestamp used for newest-first administration views. */
  joinedAt: string;
}
export interface AdminUserDetail extends AdminUserSummary {
  appAccess: boolean;
  purchasePlatforms: Array<'jvzoo' | 'launchpadjv' | 'gumroad' | 'admin'>;
  credits: CreditBalance;
  publisherCredits: CreditBalance;
}
export interface AdminUsersQuery {
  query: string;
  page: number;
  limit: number;
}
export interface AdminUsersPage {
  items: AdminUserSummary[];
  total: number;
  page: number;
  limit: number;
}
export interface DeletedAccountArchiveSummary {
  id: string;
  originalUserId: string;
  email: string;
  name: string;
  deletedAt: string;
  source: 'self' | 'admin';
  deletedByUserId?: string;
  reason?: string;
  detail?: string;
  audioObjectCount: number;
  audioDeleteAfter: string;
}
export interface DeletedAccountArchive extends DeletedAccountArchiveSummary {
  snapshot: Record<string, unknown>;
}
export type AdminEntitlementAction = PackageKey | `revoke:${PackageKey}`;
export type AdminUserAction = 'promote' | 'suspend' | 'restore' | AdminEntitlementAction;
export interface AdminUserEmailDraft {
  subject: string;
  heading: string;
  message: string;
}
export interface AdminUserEmailPreview {
  html: string;
}

export interface AdminPublisherReleaseSong {
  id: string;
  title: string;
  length: string;
  status: string;
  missing: boolean;
}

export interface AdminPublisherRelease {
  id: string;
  title: string;
  artistName: string;
  releaseType: PublisherReleaseType;
  status: PublisherReleaseStatus;
  workflowProgress: number;
  releaseDate: string;
  genre: string;
  language: string;
  explicit: boolean;
  writerContributor?: string;
  rightsAttested?: boolean;
  outletKeys: string[];
  externalId?: number | null;
  providerReviewStatus?: string | null;
  providerDeliveryStatus?: string | null;
  barcode?: string | null;
  failureReason?: string | null;
  createdAt: string;
  updatedAt: string;
  songs: AdminPublisherReleaseSong[];
  platforms: PublisherReleasePlatform[];
}

export function parseAdminEntitlementAction(
  action: unknown
): { key: PackageKey; enabled: boolean } | null {
  if (typeof action !== 'string') return null;
  if ((PACKAGE_KEYS as readonly string[]).includes(action)) {
    return { key: action as PackageKey, enabled: true };
  }
  if (!action.startsWith('revoke:')) return null;
  const key = action.slice('revoke:'.length);
  return (PACKAGE_KEYS as readonly string[]).includes(key)
    ? { key: key as PackageKey, enabled: false }
    : null;
}
